// hooks/useMotifAccumulation.ts — règles de cumul par motif et état des cumuls
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    closeAccumulationRule,
    createAccumulationRule,
    deleteAccumulationRule,
    fetchAccumulationRules,
    fetchMotifAccumulationStatus,
    fetchYellowSeriesStatus,
    type CreateAccumulationRuleInput,
} from "../services/motifAccumulationService";
import {
    pendingMotifRows,
    ruleAppliesToSeason,
    type AccumulationRule,
    type MotifStatusRow,
} from "../lib/disciplinary/motifAccumulation";
import { useLeagueSeasonStore } from "../state/useLeagueSeasonStore";
import type { YellowSeriesRow } from "../lib/disciplinary/yellowSeries";

export const ACCUMULATION_RULES_KEY = "accumulationRules";
/** Also in SANCTION_DERIVED_QUERY_KEYS: any sanction write refreshes it. */
export const MOTIF_ACCUMULATION_STATUS_KEY = "motifAccumulationStatus";

export function useAccumulationRules(leagueIds: string[], opts: { enabled?: boolean } = {}) {
    const ids = [...leagueIds].sort();
    return useQuery<AccumulationRule[]>({
        queryKey: [ACCUMULATION_RULES_KEY, ids],
        queryFn: () => fetchAccumulationRules(ids),
        enabled: (opts.enabled ?? true) && ids.length > 0,
        staleTime: 5 * 60 * 1000,
    });
}

/** Also in SANCTION_DERIVED_QUERY_KEYS. */
export const YELLOW_SERIES_STATUS_KEY = "yellowSeriesStatus";

export function useYellowSeriesStatus(leagueId: string | null | undefined, season: string | null | undefined) {
    return useQuery<YellowSeriesRow[]>({
        queryKey: [YELLOW_SERIES_STATUS_KEY, leagueId, season],
        queryFn: () => fetchYellowSeriesStatus(leagueId as string, season as string),
        enabled: !!leagueId && !!season,
        staleTime: 60 * 1000,
    });
}

export function useMotifAccumulationStatus(
    leagueId: string | null | undefined,
    season: string | null | undefined,
    opts: { enabled?: boolean } = {},
) {
    return useQuery<MotifStatusRow[]>({
        queryKey: [MOTIF_ACCUMULATION_STATUS_KEY, leagueId, season],
        queryFn: () => fetchMotifAccumulationStatus(leagueId as string, season as string),
        enabled: (opts.enabled ?? true) && !!leagueId && !!season,
        staleTime: 60 * 1000,
    });
}

function useInvalidateAccumulation() {
    const queryClient = useQueryClient();
    return () => {
        queryClient.invalidateQueries({ queryKey: [ACCUMULATION_RULES_KEY] });
        queryClient.invalidateQueries({ queryKey: [MOTIF_ACCUMULATION_STATUS_KEY] });
    };
}

export function useCreateAccumulationRule() {
    const invalidate = useInvalidateAccumulation();
    return useMutation({
        mutationFn: (input: CreateAccumulationRuleInput) => createAccumulationRule(input),
        onSuccess: invalidate,
    });
}

export function useCloseAccumulationRule() {
    const invalidate = useInvalidateAccumulation();
    return useMutation({
        mutationFn: ({ id, seasonTo }: { id: string; seasonTo: string | null }) => closeAccumulationRule(id, seasonTo),
        onSuccess: invalidate,
    });
}

export function useDeleteAccumulationRule() {
    const invalidate = useInvalidateAccumulation();
    return useMutation({
        mutationFn: (id: string) => deleteAccumulationRule(id),
        onSuccess: invalidate,
    });
}

export interface MotifPendingItem {
    row: MotifStatusRow;
    rule: AccumulationRule;
}

/**
 * Bell source (Discipline): generated suspensions not yet in a published PV,
 * and caduc thresholds, for the selected league × season.
 */
export function useMotifAccumulationPending(enabled: boolean) {
    const leagueId = useLeagueSeasonStore((s) => s.currentLeagueId);
    const season = useLeagueSeasonStore((s) => s.currentSeason);
    const { data: rules = [] } = useAccumulationRules(leagueId ? [leagueId] : [], { enabled });
    const inForce = useMemo(() => rules.filter((r) => ruleAppliesToSeason(r, season)), [rules, season]);
    const query = useMotifAccumulationStatus(leagueId, season, { enabled: enabled && inForce.length > 0 });
    const data = useMemo<MotifPendingItem[] | undefined>(() => {
        if (!enabled || !query.data) return undefined;
        const byId = new Map(inForce.map((r) => [r.id, r]));
        return pendingMotifRows(query.data)
            .filter((row) => byId.has(row.ruleId))
            .map((row) => ({ row, rule: byId.get(row.ruleId) as AccumulationRule }));
    }, [enabled, query.data, inForce]);
    return { data };
}
